/**
 * Real Parish Boundary Data from GADM v4.1
 * This file contains the actual geographic boundaries for all 14 Jamaican parishes
 */

import parishData from '../data/jamaica_parishes_real.json';

/**
 * Check if a point is inside any parish boundary
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {boolean} True if point is within any parish
 */
export const isPointInJamaica = (lat, lon) => {
  // Check if point is in any parish
  for (const parish of parishData.parishes) {
    if (isPointInParish(lat, lon, parish)) {
      return true;
    }
  }
  return false;
};

/**
 * Check if a point is inside a specific parish
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {Object} parish - Parish object with boundaries
 * @returns {boolean} True if point is within parish
 */
const isPointInParish = (lat, lon, parish) => {
  if (!parish.boundaries || !parish.boundaries.coordinates) {
    return false;
  }

  const { type, coordinates } = parish.boundaries;

  if (type === 'MultiPolygon') {
    // MultiPolygon: array of polygons
    for (const polygon of coordinates) {
      // Each polygon is an array of rings (first is outer, rest are holes)
      if (polygon.length > 0 && isPointInPolygon(lat, lon, polygon[0])) {
        return true;
      }
    }
  } else if (type === 'Polygon') {
    // Polygon: array of rings
    if (coordinates.length > 0 && isPointInPolygon(lat, lon, coordinates[0])) {
      return true;
    }
  }

  return false;
};

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude  
 * @param {Array} polygon - Array of [lon, lat] coordinates (GeoJSON format)
 * @returns {boolean} True if point is inside polygon
 */
const isPointInPolygon = (lat, lon, polygon) => {
  if (!polygon || polygon.length === 0) {
    return false;
  }

  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [lonI, latI] = polygon[i];
    const [lonJ, latJ] = polygon[j];
    
    // Skip invalid coordinates
    if (typeof latI === 'undefined' || typeof lonI === 'undefined' ||
        typeof latJ === 'undefined' || typeof lonJ === 'undefined') {
      continue;
    }
    
    const intersect = ((lonI > lon) !== (lonJ > lon)) &&
      (lat < (latJ - latI) * (lon - lonI) / (lonJ - lonI) + latI);
    
    if (intersect) inside = !inside;
  }
  
  return inside;
};

/**
 * Get all parish boundaries for visualization
 * @returns {Array} Array of parish boundary data
 */
export const getAllParishBoundaries = () => {
  return parishData.parishes.map(parish => ({
    name: parish.name,
    capital: parish.capital,
    boundaries: parish.boundaries,
    coordinates: parish.coordinates
  }));
};

/**
 * Find which parish a point belongs to
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {string|null} Parish name or null if not in any parish
 */
export const getParishForPoint = (lat, lon) => {
  for (const parish of parishData.parishes) {
    if (isPointInParish(lat, lon, parish)) {
      return parish.name;
    }
  }
  return null;
};
